"use client";
import { useSearchParams } from "next/navigation";
import { fns } from "cy-util";

export type RouteState = Record<string, string | undefined>;

/**
 * NextJS App Router hook to store state info in the url.
 *
 * State changes push a new history entry via the History API which updates
 * `useSearchParams` without a server round-trip.
 */
export function useRouteState<T extends RouteState = RouteState>(
  defaultState: T | undefined = undefined,
) {
  // null outside an App Router tree (e.g. Pages Router storybook/tests)
  const searchParams = useSearchParams() ?? new URLSearchParams();

  function setState(stateOrFn: T | ((state: T) => T)) {
    const url = new URL(window.location.href);
    let state;
    if (typeof stateOrFn === "function") {
      state = stateOrFn(getState());
    } else {
      state = stateOrFn;
    }
    for (const key of Object.keys(state)) {
      const value = state[key];
      if (value == null) {
        url.searchParams.delete(key);
      } else {
        url.searchParams.set(key, value);
      }
    }

    const newUrl = url.pathname + url.search;
    window.history.pushState(null, "", newUrl);
  }

  function getValue(key: string): string | undefined {
    const value = searchParams.get(key) ?? undefined;
    if (value === undefined && defaultState) return defaultState[key];
    return value;
  }

  function getState(): T {
    const state: Record<string, string | undefined> = {};
    if (defaultState) {
      for (const key of Object.keys(defaultState)) {
        state[key] = getValue(key);
      }
    }
    for (const [key] of searchParams) {
      state[key] = getValue(key);
    }
    return state as T;
  }

  const state = getState();

  function useState(key: string, defaultValue?: string) {
    const _setState = (
      action:
        | string
        | undefined
        | ((val: string | undefined) => string | undefined),
    ) => {
      if (typeof action === "function") {
        const newState = { ...state, [key]: action(state[key]) };
        setState(newState);
      } else {
        setState((x) => ({ ...x, [key]: action }));
      }
    };

    const currentValue = fns.ifElse(
      (x) => x == null,
      fns.always(defaultValue),
      fns.identity,
      state[key],
    );
    return [currentValue, _setState] as const;
  }

  return { state, setState, useState } as const;
}
